import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import deprecated from '../../utils/deprecated'

/**
 * @callback onPressedChangeCB
 * @param {boolean} pressed - new pressed state (true='on', false='off')
 * @return {void}
 */

/**
 * The FavoriteButton component is a toggle button with pressed states ('on', 'off').
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-favorite-button.html
 *
 * variants:
 * - active: initial pressed/active state.
 * - reversed: reverse order of label and icon.
 * - icon only: label is hidden.
 *
 * @param {string} [className] - Additional classes on main button element, @see 'fav-button-modifier'.
 * @param {string} [labelOn] - Label shown with 'on' state, @see 'fav-button-label-on'.
 * @param {string} [labelOff] - Label shown with 'off' state, @see 'fav-button-label-off'.
 * @param {boolean} [active] - Active variant, @see https://bronson.vwfs.tools/default/components/detail/bronson-favorite-button--favorite-button-active.html
 * @param {boolean} [pressed] - Deprecated, use {@link active} instead.
 * @param {boolean} [reversed] - Reversed variant, @see https://bronson.vwfs.tools/default/components/detail/bronson-favorite-button--favorite-button-reversed.html
 * @param {boolean} [iconOnly] - Icon only variant, @see https://bronson.vwfs.tools/default/components/detail/bronson-favorite-button--favorite-button-icon-only.html
 * @param {string} [testId] - `[data-testid]` attribute on main button element.
 * @param {onPressedChangeCB} [onPressedChange] - Callback function when button is pressed.
 * @param {Object} [otherProps] - Other props to be passed to main button element.
 * @return {JSX.Element} - The {@link FavoriteButton} component.
 */
export function FavoriteButton({
  className,
  labelOn,
  labelOff,
  reversed,
  iconOnly,
  testId,
  active,
  pressed,
  onPressedChange,
  ...otherProps
}) {
  /**
   * Set the internal state based on {@link active} or the deprecated {@link pressed} state.
   * If `active` is set and a boolean it takes precedence over {@link pressed}.
   * @type {boolean}
   */
  const externalPressed = active ?? pressed ?? false

  /**
   * Track the active state.
   */
  const [isActive, setIsActive] = useState(externalPressed)

  useEffect(() => {
    setIsActive(externalPressed)
  }, [externalPressed])

  function togglePressed() {
    setIsActive(!isActive)
    onPressedChange?.(!isActive)
  }

  const buttonClassNameList = classNames(
    'c-fav-button',
    'js-fav-button',
    {
      'c-fav-button--reversed': reversed,
      'c-fav-button--icon-only': iconOnly,
    },
    className
  ).trim()

  // generated main result
  return (
    <button
      {...otherProps}
      className={buttonClassNameList}
      data-testid={testId}
      aria-pressed={isActive ? 'true' : 'false'} // force attribute appearance also when 'false'
      onClick={() => togglePressed()}
      type="button"
    >
      <span className="c-fav-button__icon" />
      <span className="c-fav-button__label c-fav-button__label--on">{labelOn}</span>
      <span className="c-fav-button__label c-fav-button__label--off">{labelOff}</span>
    </button>
  )
}

FavoriteButton.propTypes = {
  className: PropTypes.string, // Additional CSS classes on the component element.
  labelOn: PropTypes.node, // Bronson template: 'fav-button-label-on'.
  labelOff: PropTypes.node, // Bronson template: 'fav-button-label-off'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
  onPressedChange: PropTypes.func,
  /* Convenience props */
  active: PropTypes.bool, // Convenience prop for Bronson template: 'active'.
  iconOnly: PropTypes.bool, // Convenience prop for `.c-fav-button--icon-only` (Bronson template: 'reversed').
  reversed: PropTypes.bool, // Convenience prop for `.c-fav-button--reversed` (Bronson template: 'icon-only').
  /* Deprecated props */
  pressed: deprecated(PropTypes.bool, 'Please use "active" instead. Prop will be removed in Bronson-React v6.0.0.'), // Deprecated since Bronson-React 3.1.0
}
